import { ChangeEventHandler, KeyboardEventHandler, useEffect, useState } from 'react';

import { QuerySavePopoverProps } from './QuerySavePopover.types';

export interface UseQuerySavePopoverResult {
  value: string;
  onSave: () => void;
  onChange: ChangeEventHandler<HTMLInputElement>;
  getPopoverProps: () => any;
  getRootProps: () => any;
  getInputProps: () => any;
}

export const useQuerySavePopover = ({
  inputValue,
  onSave,
  onClose,
  ...popoverProps
}: QuerySavePopoverProps): UseQuerySavePopoverResult => {
  const { open } = popoverProps;
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
  const [value, setValue] = useState('');

  const handleSave = () => {
    if (value) {
      onClose();
      onSave(value);
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    switch (event.key) {
      case 'Enter':
        // Wait until IME is settled.
        if (event.which === 229) {
          break;
        }
        event.preventDefault();
        event.stopPropagation();
        handleSave();
        break;
      case 'Escape':
        // Avoid Opera to exit fullscreen mode.
        event.preventDefault();
        // Avoid the Modal to handle the event.
        event.stopPropagation();
        if (value) {
          setValue('');
        } else {
          onClose();
        }
        break;
    }
  };

  const onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setValue(event.target.value);
  };

  useEffect(() => {
    if (open && inputRef) {
      inputRef.focus();
    }
  }, [open, inputRef]);

  useEffect(() => {
    if (inputValue !== undefined) {
      setValue(inputValue);
    }
  }, [inputValue]);

  return {
    value,
    onChange,
    onSave: handleSave,
    getPopoverProps: () => ({
      ...popoverProps,
      onClose,
    }),
    getRootProps: () => ({
      onKeyDown: handleKeyDown,
    }),
    getInputProps: () => ({
      ref: setInputRef as any,
    }),
  };
};
